/**
 * Realtime contract types derived from the upstream RMF API and the
 * Zenoh key expression namespace used by `zenoh-plugin-rest`.
 *
 * Sources:
 * - packages/api-client/lib/index.ts (SioClient rooms, historical)
 * - packages/api-server routes (Python source) (@router.sub rooms (historical))
 * - rmf_api_msgs JSON schemas (rmf_api_msgs) (payload shapes)
 * - packages/rmf-dashboard-framework/src/services/robot-trajectory-manager.ts
 *
 * Phase 0 (`.kilo/plans/1783657918677-nexsoss-rmf-web-phase0-zenoh-bringup.md`):
 * the SPA realtime transport is **Zenoh-native SSE** over `zenoh-plugin-rest`.
 * One broad subscription on `rmf/sites/{site}/**` is opened; per-entity
 * subscriptions are registered as patterns the dispatcher matches against
 * the incoming `key` field. Wildcards are Zenoh-syntax: `*` (single level)
 * and `**` (multi level).
 *
 * Socket.IO types are retained for export-compatibility and for any
 * transition-window consumers; new code should use the
 * `SubscriptionKey` builders in §"Subscription key taxonomy" below and
 * the `ZenohSseTransport` abstraction in `src/api/zenoh-sse.ts`.
 */

import type { components } from './generated/openapi';
import type { RmfApiMsgs } from './generated/rmf-api-msgs';

// ---------------------------------------------------------------------------
// Socket.IO subscription control (legacy / transition window)
// ---------------------------------------------------------------------------

export interface SocketIOSubscribeMessage {
  room: SocketIORoom;
}

export interface SocketIOUnsubscribeMessage {
  room: SocketIORoom;
}

// ---------------------------------------------------------------------------
// Socket.IO rooms / event names (legacy / transition window)
// ---------------------------------------------------------------------------

export type SocketIORoom =
  | '/alerts/requests'
  | '/alerts/responses'
  | '/beacons'
  | '/building_map'
  | '/building_map/fire_alarm_trigger'
  | '/delivery_alerts'
  | `/doors/${string}/state`
  | `/lifts/${string}/state`
  | `/dispensers/${string}/state`
  | `/ingestors/${string}/state`
  | `/fleets/${string}/state`
  | `/fleets/${string}/log`
  | `/tasks/${string}/state`
  | `/tasks/${string}/log`
  | '/rios';

// ---------------------------------------------------------------------------
// Payload types for each room / topic
// ---------------------------------------------------------------------------

export type BeaconState = components['schemas']['BeaconState'];
export type BuildingMap = components['schemas']['BuildingMap'];
export type FireAlarmTriggerState = components['schemas']['FireAlarmTriggerState'];
export type DoorState = components['schemas']['DoorState'];
export type LiftState = components['schemas']['LiftState'];
export type DispenserState = components['schemas']['DispenserState'];
export type IngestorState = components['schemas']['IngestorState'];
export type FleetState = components['schemas']['FleetState'];
export type FleetLog = components['schemas']['FleetLog'];
export type TaskStateOutput = components['schemas']['TaskState-Output'];
export type TaskEventLog = components['schemas']['TaskEventLog'];
export type AlertRequest = components['schemas']['AlertRequest'];
export type AlertResponse = components['schemas']['AlertResponse'];
export type DeliveryAlert = components['schemas']['DeliveryAlert'];
export type Rio = components['schemas']['Rio'];

// Schema-derived state shapes (rmf_api_msgs) for cross-checking.
export type TaskState = RmfApiMsgs['task_state'];
export type TaskStateUpdate = RmfApiMsgs['task_state_update'];
export type FleetStateUpdate = RmfApiMsgs['fleet_state_update'];
export type TaskLogUpdate = RmfApiMsgs['task_log_update'];

// ---------------------------------------------------------------------------
// Trajectory server (retained Zenoh key per map)
// ---------------------------------------------------------------------------

// RawVelocity and RawPose2D are tuples [x, y, theta].
export type RawVelocity = [number, number, number];
export type RawPose2D = [number, number, number];

export interface RawKnot {
  /** milliseconds */
  t: number;
  v: RawVelocity;
  x: RawPose2D;
}

export interface Trajectory {
  id: number;
  shape: string;
  dimensions: number;
  segments: RawKnot[];
  robot_name: string;
  fleet_name: string;
  map_name: string;
}

export type Conflict = number[];

// `rmf-state-coalescer` republishes a snapshot that already unions all
// robots' latest trajectories for the requested `map_name`; the SPA does
// not need a request/response shape any more.
export interface TrajectorySnapshot {
  map_name: string;
  trajectories: Trajectory[];
  conflicts: Conflict[];
  generated_at: number;
  /**
   * "Now" in the same clock domain as each segment's `t` (schedule-node
   * time, which respects use_sim_time — NOT `generated_at`, which is
   * always wall-clock). Used to split a trajectory into its
   * already-traversed vs still-planned portions for rendering. Optional
   * for backward compatibility with older publisher builds; renderers
   * should fall back to drawing the whole path uniformly when absent.
   */
  now_ms?: number;
  /**
   * Publisher-side staleness (nexsoss-rmf trajectory publisher, sent
   * alongside `now_ms`): `true` when the publisher's schedule mirror
   * has not been updated within its watchdog window, so `trajectories`
   * reflects an empty or old mirror rather than what the robots are
   * actually doing. `useLiveData` keeps the last non-stale snapshot in
   * the cache while this is set and raises the top-bar "trajectory
   * feed stale" chip instead. Older publishers omit it (= fresh).
   */
  stale?: boolean;
  /** Age of the publisher's schedule mirror when the snapshot was
   *  built, ms. Only meaningful alongside `stale`. */
  mirror_age_ms?: number;
}

// ---------------------------------------------------------------------------
// Generic envelopes
// ---------------------------------------------------------------------------

export interface SocketIOEventEnvelope<T = unknown> {
  room: SocketIORoom;
  payload: T;
  ts: number;
}

export interface LiveEventEnvelope<T = unknown> {
  /** Zenoh key expression the message arrived on (e.g.
   *  `rmf/sites/site-alpha/fleets/alpha/state`). */
  key: SubscriptionKey;
  payload: T;
  /** Wall-clock ms when the dispatcher observed the message. */
  ts: number;
}

// ---------------------------------------------------------------------------
// Subscription key taxonomy (per ADR 0007)
// ---------------------------------------------------------------------------

/**
 * Subscription keys are always scoped under `rmf/sites/{site}/...`. The site
 * is the SPA site-selector value. Use the `*Key` builders below to construct
 * keys — never concatenate strings ad hoc, so the Zenoh namespace stays in
 * sync with the upstream RMF core publishers.
 */
export const SUBSCRIPTION_PREFIX = 'rmf/sites';

/** Reject Zenoh wildcards (`*`, `**`) and topic-level separators in any
 * value used as a key segment. Returns the value unchanged if valid,
 * otherwise throws. */
export function assertSafeKeySegment(name: string, value: string): void {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`Invalid subscription key segment for ${name}: must be a non-empty string`);
  }
  if (value.includes('/') || value.includes('*')) {
    throw new Error(`Invalid subscription key segment for ${name}: ${JSON.stringify(value)} contains a wildcard or '/'`);
  }
}

/**
 * `live.subscribe()`'s underlying transport doesn't actually filter by
 * this pattern (see its own doc comment) — it just echoes the pattern
 * back as `env.key` for legacy consumers that don't need per-key
 * dispatch. The real per-map identity comes from the `TrajectorySnapshot`
 * payload's own `map_name` field. This builder exists so the pattern
 * string at least documents the real backend key shape
 * (`rmf/v1/sites/{site}/trajectories/{map}/state`, published by
 * nexsoss-rmf's trajectory publisher via the worker's reprojection)
 * rather than a stale pre-v1 one.
 */
export function trajectoryKey(site: string): SubscriptionKey {
  assertSafeKeySegment('site', site);
  return `rmf/v1/sites/${site}/trajectories/*/state` as SubscriptionKey;
}
export function fleetStateKey(site: string, fleet: string): SubscriptionKey {
  assertSafeKeySegment('site', site);
  assertSafeKeySegment('fleet', fleet);
  return `${SUBSCRIPTION_PREFIX}/${site}/fleets/${fleet}/state` as SubscriptionKey;
}
export function fleetLogKey(site: string, fleet: string): SubscriptionKey {
  assertSafeKeySegment('site', site);
  assertSafeKeySegment('fleet', fleet);
  return `${SUBSCRIPTION_PREFIX}/${site}/fleets/${fleet}/log` as SubscriptionKey;
}
export function doorStateKey(site: string, name: string): SubscriptionKey {
  assertSafeKeySegment('site', site);
  assertSafeKeySegment('door name', name);
  return `${SUBSCRIPTION_PREFIX}/${site}/doors/${name}/state` as SubscriptionKey;
}
export function liftStateKey(site: string, name: string): SubscriptionKey {
  assertSafeKeySegment('site', site);
  assertSafeKeySegment('lift name', name);
  return `${SUBSCRIPTION_PREFIX}/${site}/lifts/${name}/state` as SubscriptionKey;
}
// The dispenser/ingestor/beacon/RIOS key builders that used to sit
// here were deleted along with the Devices Systems panel: nothing has
// ever published those categories on either key plane (no worker
// projection, no api-server publisher), so every subscription they
// built matched silence.
export function buildingMapSubscriptionKey(site: string): SubscriptionKey {
  assertSafeKeySegment('site', site);
  return `${SUBSCRIPTION_PREFIX}/${site}/building_map` as SubscriptionKey;
}
/**
 * Fire mode's live key. On the v1 plane, unlike its neighbours here:
 * the api-server publishes it at `rmf/v1/sites/{site}/emergency/fire/
 * state` (`backend/api_server/fire_mode.py::fire_alarm_key`), which is
 * the only prefix the bus ACL, the storage manager and the SPA's SSE
 * transport all admit.
 *
 * Consumers should match on the `fire_alarm` event kind rather than on
 * this string — `fetchSseTransport().subscribe()` does not filter by
 * the pattern it is given.
 */
export function fireAlarmTriggerSubscriptionKey(site: string): SubscriptionKey {
  assertSafeKeySegment('site', site);
  return `rmf/v1/sites/${site}/emergency/fire/state` as SubscriptionKey;
}
export function taskStateKey(site: string, id: string): SubscriptionKey {
  assertSafeKeySegment('site', site);
  assertSafeKeySegment('task id', id);
  return `${SUBSCRIPTION_PREFIX}/${site}/tasks/${id}/state` as SubscriptionKey;
}
export function taskLogKey(site: string, id: string): SubscriptionKey {
  assertSafeKeySegment('site', site);
  assertSafeKeySegment('task id', id);
  return `${SUBSCRIPTION_PREFIX}/${site}/tasks/${id}/log` as SubscriptionKey;
}
export function alertRequestsSubscriptionKey(site: string): SubscriptionKey {
  assertSafeKeySegment('site', site);
  return `${SUBSCRIPTION_PREFIX}/${site}/alerts/requests` as SubscriptionKey;
}
export function alertResponsesSubscriptionKey(site: string): SubscriptionKey {
  assertSafeKeySegment('site', site);
  return `${SUBSCRIPTION_PREFIX}/${site}/alerts/responses` as SubscriptionKey;
}

/**
 * Wildcard subscription pattern for everything under a site
 * (`rmf/sites/{site}/**`). Used by the SPA's single broad SSE
 * subscription.
 */
export function siteSubscriptionPattern(site: string): SubscriptionPattern {
  assertSafeKeySegment('site', site);
  return `${SUBSCRIPTION_PREFIX}/${site}/**` as SubscriptionPattern;
}

/**
 * Wildcard subscription pattern for all fleet state keys under a site.
 * Matches `rmf/sites/{site}/fleets/<asterisk>/state` and is used by the
 * app-level live data overlay to pick up fleet state regardless of
 * how many fleets exist (or are added later). Use this with
 * `SubscriptionPattern`, not `SubscriptionKey` — wildcard characters
 * are rejected by the exact-key builders above.
 */
export function fleetStateWildcardPattern(site: string): SubscriptionPattern {
  assertSafeKeySegment('site', site);
  return `${SUBSCRIPTION_PREFIX}/${site}/fleets/*/state` as SubscriptionPattern;
}

/**
 * Wildcard subscription pattern for all task state keys under a site.
 * Matches `rmf/sites/{site}/tasks/<asterisk>/state`. Use with `SubscriptionPattern`.
 */
export function taskStateWildcardPattern(site: string): SubscriptionPattern {
  assertSafeKeySegment('site', site);
  return `${SUBSCRIPTION_PREFIX}/${site}/tasks/*/state` as SubscriptionPattern;
}

/**
 * Wildcard subscription pattern for all door state keys under a site.
 * Matches `rmf/sites/{site}/doors/<asterisk>/state`.
 */
export function doorStateWildcardPattern(site: string): SubscriptionPattern {
  assertSafeKeySegment('site', site);
  return `${SUBSCRIPTION_PREFIX}/${site}/doors/*/state` as SubscriptionPattern;
}

/**
 * Wildcard subscription pattern for all lift state keys under a site.
 * Matches `rmf/sites/{site}/lifts/<asterisk>/state`.
 */
export function liftStateWildcardPattern(site: string): SubscriptionPattern {
  assertSafeKeySegment('site', site);
  return `${SUBSCRIPTION_PREFIX}/${site}/lifts/*/state` as SubscriptionPattern;
}

/**
 * Wildcard subscription pattern for a trajectory key on a specific
 * map. Matches the exact key `rmf/sites/{site}/trajectory/{map}` (no
 * Zenoh wildcards are required because the key has no variable
 * segments beyond the map).
 */
export function trajectoryKeyPattern(site: string, map: string): SubscriptionPattern {
  assertSafeKeySegment('site', site);
  assertSafeKeySegment('map', map);
  return `${SUBSCRIPTION_PREFIX}/${site}/trajectory/${map}` as SubscriptionPattern;
}

export type SubscriptionKey = string & { readonly __brand: 'SubscriptionKey' };
/** A Zenoh key expression with optional wildcards (`*` / `**`).
 *  Exact keys (e.g. `rmf/sites/site-alpha/beacons`) are a degenerate
 *  subset and can be passed where a pattern is expected. */
export type SubscriptionPattern = string & { readonly __brand: 'SubscriptionKey' | 'SubscriptionPattern' };

/**
 * Live state for the legacy Phase-0 realtime transport. Kept here
 * so the legacy hooks keep compiling during the Phase-0 → v1
 * migration. The convergence target uses `FetchSseState` from
 * `./fetch-sse`.
 *
 * @deprecated use FetchSseState from './fetch-sse'.
 */
export type LiveState = 'idle' | 'connecting' | 'open' | 'closed' | 'stale';
